/**
 * Client side of the action history (#278) that the API layer needs.
 *
 * Action groups: some user actions take more than one request - turning
 * selected text into a task creates the task, then the document autosaves with
 * the selection replaced. startActionGroup() arms a group id; every write
 * carries it as X-Action-Group until the next document autosave has gone out
 * (or 30 s pass), so the server records the whole thing as one undo step.
 *
 * Recent deletes: the server names each recorded action in X-Action-Id. The
 * last DELETE's action is kept for a moment so the success toast that the
 * calling page shows next can offer "Undo" without every delete site knowing
 * about history.
 */

const GROUP_TTL_MS = 30_000;
const DELETE_UNDO_WINDOW_MS = 1500;

/** Fields an autosave PATCH carries - matches the server's typing detection. */
const CONTENT_FIELDS = new Set(['content', 'description', 'note_content', 'summary', 'template_content']);

let group: { id: string; until: number; saves: number } | null = null;
let recentDelete: { id: string; at: number } | null = null;

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-4000-8000-${Math.random().toString(16).slice(2, 14)}`;
}

/**
 * Arms a group. It ends after `saves` document autosaves (one by default; an
 * excerpt sent into documents open in this tab also waits for their saves).
 */
export function startActionGroup(saves = 1): string {
  group = { id: newId(), until: Date.now() + GROUP_TTL_MS, saves: Math.max(1, saves) };
  return group.id;
}

export function endActionGroup(): void {
  group = null;
}

export function isContentSave(method: string, body: unknown): boolean {
  if (method !== 'PATCH' || !body || typeof body !== 'object' || Array.isArray(body)) return false;
  const keys = Object.keys(body);
  return keys.length > 0 && keys.every((k) => CONTENT_FIELDS.has(k));
}

/** Header to send with a write, if a group is armed. Ends the group after its autosave. */
export function groupHeaderFor(method: string, body: unknown): Record<string, string> {
  if (!group) return {};
  if (Date.now() > group.until) {
    group = null;
    return {};
  }
  const header = { 'X-Action-Group': group.id };
  if (isContentSave(method, body) && --group.saves <= 0) group = null;
  return header;
}

/** Called by the API layer with every write's response. */
export function noteActionResponse(method: string, res: { headers?: { get?(name: string): string | null } }): void {
  const id = res.headers?.get?.('X-Action-Id') ?? null;
  if (method === 'DELETE' && id) recentDelete = { id, at: Date.now() };
}

/** The action of a DELETE that completed within the last moment, consumed once. */
export function takeRecentDeleteAction(now = Date.now()): string | null {
  const d = recentDelete;
  recentDelete = null;
  return d && now - d.at <= DELETE_UNDO_WINDOW_MS ? d.id : null;
}
